import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axiosConfig';
import auditService from '../../services/auditService';
import { calculateSftForSlabs, getProductLots } from '../../utils/lotUtils';
import { getProductPrimaryImageUrl } from '../../utils/imageUtils';

// Prefer the Cloudinary small-thumbnail variant (falls back to the raw primary image URL)
const getCartImage = (product) => getProductPrimaryImageUrl(product, false, 'small');

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
const PROMOTION_STORAGE_KEY = 'global_promotions';

// Helper functions
const calculateItemTotal = (item) => {
  const price = item.discountedPrice || item.price;
  return price * item.quantity;
};

// Selected SFT for a regular (per-SFT priced) cart item: SFT covered by the chosen slabs.
// Returns 0 when it can't be computed (booking items, missing lots or per-SFT price).
export const getCartItemSelectedSft = (item) => {
  if (!item || item.isLotBooking || item.isAdvanceBooking) return 0;
  if (!(Number(item.pricePerSft) > 0)) return 0;
  const sft = calculateSftForSlabs(item.sftLots, Number(item.quantity) || 0);
  if (sft > 0) return sft;
  // Items synced from the backend cart carry selectedSft until their lots are loaded
  return Number(item.selectedSft) > 0 ? Number(item.selectedSft) : 0;
};

const getCurrentCustomerId = (state) => {
  const user = state.auth?.user || state.customer?.profile;
  return user?.id || user?._id || null;
};

// Build a frontend cart item from a backend CartItem (used when the item isn't in local storage,
// e.g. it was added from another device).
const cartItemFromServer = (si) => {
  const productId = si.productId;
  const isLot = !!(si.lotBooking ?? si.isLotBooking);
  const isAdvance = !!(si.advanceBooking ?? si.isAdvanceBooking);
  const quantity = Number(si.quantity) || 0;
  const unitPrice = Number(si.unitPrice) || 0;
  const selectedSft = Number(si.selectedSft) || 0;
  const regular = !isLot && !isAdvance;
  const item = {
    itemKey: isLot ? `${productId}_lot_server` : `${productId}_${JSON.stringify(si.customizations || {})}`,
    id: productId,
    name: si.productName,
    price: regular && selectedSft > 0 && quantity > 0 ? (selectedSft * unitPrice) / quantity : unitPrice,
    discountedPrice: null,
    image: si.productImage,
    category: si.category,
    subCategory: si.subCategory,
    sellerId: si.sellerId,
    sellerName: si.sellerName,
    priceUnit: si.priceUnit,
    stockQuantity: si.availableQuantity,
    pricePerSft: regular ? unitPrice : undefined,
    selectedSft: regular && selectedSft > 0 ? selectedSft : undefined,
    specifications: si.customizations || {},
    quantity,
    addedAt: si.addedAt,
  };
  if (isAdvance || isLot) {
    item.isAdvanceBooking = isAdvance;
    item.originalPrice = Number(si.originalPrice) || 0;
    item.advancePercentage = Number(si.advancePercentage) || 10;
  }
  if (isLot) {
    const advancePercentage = Number(si.advancePercentage) || 10;
    const advanceAmount = Number(si.advanceAmount) || 0;
    const totalLotPrice = advanceAmount * 100 / advancePercentage;
    item.isLotBooking = true;
    item.lotSft = item.originalPrice > 0 ? totalLotPrice / item.originalPrice : 1;
    item.totalLotPrice = totalLotPrice;
    item.paymentSplit = {
      advancePercentage,
      advanceAmount,
      remainingAmount: Number(si.remainingAmount) || 0,
      totalAmount: totalLotPrice,
    };
    item.lots = [];
    item.uiLotCount = 1;
  }
  return item;
};

// Base amount (excl. GST) of a regular cart item: selectedSft × pricePerSft,
// falling back to price × quantity when the selected SFT is unknown.
export const getCartItemAmount = (item) => {
  // Lot bookings: the amount payable now is the advance on the whole lot. item.quantity is the
  // piece count across the selected lots, so it must not be used as a multiplier here.
  if (item?.isLotBooking) {
    const advancePct = Number(item.paymentSplit?.advancePercentage || item.advancePercentage) || 10;
    const totalLotPrice = Number(item.originalPrice || 0) * Number(item.lotSft || 1);
    if (totalLotPrice > 0) return (totalLotPrice * advancePct) / 100;
    return Number(item.paymentSplit?.advanceAmount) || 0;
  }
  const sft = getCartItemSelectedSft(item);
  if (sft > 0) return sft * Number(item.pricePerSft);
  return Number(item.discountedPrice || item.price) * item.quantity;
};

const calculateCartTotals = (items) => {
  // Calculate subtotal with GST-inclusive prices
  const subtotalWithGST = items.reduce((total, item) => {
    let itemBasePrice;
    if (item.isLotBooking) {
      // For lot bookings: price = selectedSFT × pricePerSFT — no quantity multiplier
      const unitPrice = Number(item.originalPrice || item.price);
      const lotSize = Number(item.lotSft || 1);
      itemBasePrice = unitPrice * lotSize;
    } else {
      itemBasePrice = getCartItemAmount(item);
    }
    return total + itemBasePrice * 1.18;
  }, 0);
  
  // Calculate base price (excluding GST) for display purposes
  const subtotal = subtotalWithGST / 1.18;
  const tax = subtotalWithGST - subtotal; // GST amount
  
  // Check if any item is a lot booking - exclude shipping for lot booking items
  const hasLotBookingItems = items.some(item => item.isLotBooking || item.isAdvanceBooking);
  const shipping = hasLotBookingItems ? 0 : 200; // No shipping for lot booking, otherwise ₹200
  
  const total = subtotalWithGST + shipping;

  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    tax: parseFloat(tax.toFixed(2)),
    shipping: parseFloat(shipping.toFixed(2)),
    total: parseFloat(total.toFixed(2)),
  };
};

// Get cart from localStorage for specific user
const getCartFromStorage = (userId = null) => {
  try {
    // If no userId provided, try to get from localStorage
    if (!userId) {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        userId = userData.id || userData._id;
      }
    }
    
    // If still no userId, return empty cart
    if (!userId) {
      return [];
    }
    
    const cartKey = `cart_${userId}`;
    const cart = localStorage.getItem(cartKey);
    return cart ? JSON.parse(cart) : [];
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return [];
  }
};

// Save cart to localStorage for specific user
const saveCartToStorage = (items, userId = null) => {
  try {
    // If no userId provided, try to get from localStorage
    if (!userId) {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        userId = userData.id || userData._id;
      }
    }
    
    // If still no userId, don't save
    if (!userId) {
      console.warn('Cannot save cart: No user ID available');
      return;
    }
    
    const cartKey = `cart_${userId}`;
    localStorage.setItem(cartKey, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

// Clear cart from localStorage for specific user
const clearCartFromStorage = (userId = null) => {
  try {
    // If no userId provided, try to get from localStorage
    if (!userId) {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        userId = userData.id || userData._id;
      }
    }
    
    // If still no userId, try to clear generic cart key as fallback
    if (!userId) {
      localStorage.removeItem('cart');
      return;
    }
    
    const cartKey = `cart_${userId}`;
    localStorage.removeItem(cartKey);
  } catch (error) {
    console.error('Error clearing cart from localStorage:', error);
  }
};

const loadAvailablePromotionsFromStorage = () => {
  try {
    const stored = localStorage.getItem(PROMOTION_STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    const promotions = Array.isArray(parsed) ? parsed : [];
    
    // Filter out inactive promotions, deleted promotions, and expired promotions
    const now = new Date();
    return promotions.filter((promotion) => {
      // Skip if promotion is null or undefined (deleted)
      if (!promotion) {
        return false;
      }
      
      // Check if manually set to inactive
      if (promotion.status === 'inactive') {
        return false;
      }
      
      // Check if promotion has been deleted (no id, _id, or promotionId)
      if (!promotion.id && !promotion._id && !promotion.promotionId) {
        return false;
      }

      // Check start datetime
      if (promotion.startDateTime) {
        const startDate = new Date(promotion.startDateTime);
        if (now < startDate) {
          return false;
        }
      } else if (promotion.startDate) {
        const startDate = new Date(promotion.startDate + 'T00:00:00');
        if (now < startDate) {
          return false;
        }
      }

      // Check end datetime
      if (promotion.endDateTime) {
        const endDate = new Date(promotion.endDateTime);
        if (now > endDate) {
          return false;
        }
      } else if (promotion.endDate) {
        const endDate = new Date(promotion.endDate + 'T23:59:59');
        if (now > endDate) {
          return false;
        }
      }

      return true;
    });
  } catch (error) {
    console.error('Failed to load promotions for cart:', error);
    return [];
  }
};

// Async thunk for adding to cart with backend API call
export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async ({ product, quantity = 1, specifications = {} }, { getState, dispatch, rejectWithValue }) => {
    try {
      // Get user info from auth state (assuming it's in auth slice)
      const state = getState();
      const user = state.auth?.user || state.customer?.profile;
      const customerId = user?.id;
      const productId = product?.id ?? product?._id;
      
      // Call backend API to save to database
      if (customerId && productId) {
        try {
          const requestData = { 
            productId, 
            quantity,
            customizations: specifications
          };

          // Regular products are priced per SFT: send the SFT covered by the selected slabs
          if (!product.isAdvanceBooking && !product.isLotBooking) {
            const selectedSft = calculateSftForSlabs(getProductLots(product), quantity);
            if (selectedSft > 0) requestData.selectedSft = Number(selectedSft.toFixed(4));
          }

          // Add booking details only for booking products
          if (product.isAdvanceBooking) {
            requestData.isAdvanceBooking = true;
            requestData.originalPrice = product.originalPrice;
            requestData.advancePercentage = product.advancePercentage || 10;
          }
          
          if (product.isLotBooking) {
            requestData.isLotBooking = true;
            requestData.isAdvanceBooking = true; // Lot booking is also advance booking
            requestData.originalPrice = product.originalPrice;
            requestData.advancePercentage = product.advancePercentage || 10;
            // Send advance amount and remaining amount from paymentSplit if available
            if (product.paymentSplit) {
              requestData.advanceAmount = product.paymentSplit.advanceAmount;
              requestData.remainingAmount = product.paymentSplit.remainingAmount;
            }
            // Note: Don't send full lot request data to cart API - that's handled separately
          }
          
          const response = await axios.post(`${API_BASE_URL}/cart/add`, requestData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('Cart item saved to database:', response.data);
          // Re-sync with the customer's backend cart (its response arrives after this add is applied)
          dispatch(fetchCustomerCart());

          // Log add to cart event
          try {
            const userRole = user?.role || 'GUEST';
            await auditService.logAddToCart(productId, quantity, userRole);
          } catch (logError) {
            console.error('Failed to log add to cart:', logError);
            // Don't fail the cart operation if logging fails
          }
        } catch (apiError) {
          console.error('Failed to save cart item to database:', apiError);
          // Continue with local storage even if API fails
        }
      } else {
        // Log add to cart for guest users too
        try {
          await auditService.logAddToCart(productId, quantity, 'GUEST');
        } catch (logError) {
          console.error('Failed to log add to cart:', logError);
        }
      }
      
      // Return the product data for local state update
      return { product, quantity, specifications };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const authHeaders = () => ({
  headers: {
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  }
});

// Active backend cart id: the one from the last customer-cart sync, else GET /cart
const resolveActiveCartId = async (state) => {
  if (state.cart.cartId) return state.cart.cartId;
  const cartResponse = await axios.get(`${API_BASE_URL}/cart`, authHeaders());
  return cartResponse.data?.id;
};

// Load the logged-in customer's cart from the backend so it is the same on every device
export const fetchCustomerCart = createAsyncThunk(
  'cart/fetchCustomerCart',
  async (_, { getState, rejectWithValue }) => {
    const customerId = getCurrentCustomerId(getState());
    if (!customerId) return rejectWithValue('Not logged in');
    try {
      const response = await axios.get(`${API_BASE_URL}/cart/customer/${customerId}`, authHeaders());
      const carts = response.data?.carts || [];
      // Carts are sorted by updatedAt desc; use the active one
      const activeCart = carts.find(c => c.status === 'ACTIVE') || null;
      return { cart: activeCart };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Remove an item: PUT /cart/{cartId}/items/{productId} with quantity 0, then drop it locally
export const removeCartItem = createAsyncThunk(
  'cart/removeCartItem',
  async (itemKey, { getState }) => {
    const state = getState();
    const customerId = getCurrentCustomerId(state);
    const cartItem = state.cart.items.find(item => item.itemKey === itemKey);
    if (customerId && cartItem?.id) {
      try {
        const cartId = await resolveActiveCartId(state);
        await axios.put(`${API_BASE_URL}/cart/${cartId}/items/${cartItem.id}`, { quantity: 0 }, authHeaders());
      } catch (apiError) {
        console.error('Failed to remove cart item from database:', apiError);
      }
    }
    return itemKey;
  }
);

export const updateCartItemQuantity = createAsyncThunk(
  'cart/updateCartItemQuantity',
  async ({ itemKey, quantity }, { getState, rejectWithValue }) => {
    try {
      // Get user info from auth state
      const state = getState();
      const user = state.auth?.user || state.customer?.profile;
      const customerId = user?.id;
      
      // Find the item in local state to get product ID
      const cartItem = state.cart.items.find(item => item.itemKey === itemKey);
      if (!cartItem) {
        throw new Error('Cart item not found');
      }
      
      // Call backend API to update quantity in database
      if (customerId && cartItem.id) {
        try {
          const cartId = await resolveActiveCartId(state);

          // Then update the item quantity (and the SFT those slabs cover, for per-SFT items)
          const body = { quantity };
          if (!cartItem.isLotBooking && !cartItem.isAdvanceBooking) {
            const selectedSft = getCartItemSelectedSft({ ...cartItem, quantity });
            if (selectedSft > 0) body.selectedSft = Number(selectedSft.toFixed(4));
          }
          const updateResponse = await axios.put(
            `${API_BASE_URL}/cart/${cartId}/items/${cartItem.id}`,
            body,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          console.log('Cart item quantity updated in database:', updateResponse.data);
        } catch (apiError) {
          console.error('Failed to update cart item quantity in database:', apiError);
          // Continue with local storage even if API fails
        }
      }
      
      // Return the data for local state update
      return { itemKey, quantity };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      // Get user info from auth state
      const state = getState();
      const user = state.auth?.user || state.customer?.profile;
      const customerId = user?.id;
      
      // Call backend API to clear cart in database
      if (customerId) {
        try {
          // First get the active cart to get the cart ID
          const cartResponse = await axios.get(`${API_BASE_URL}/cart`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          const cart = cartResponse.data;
          
          // Then clear the cart
          const clearResponse = await axios.delete(
            `${API_BASE_URL}/cart/${cart.id}/clear`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          console.log('Cart cleared in database:', clearResponse.data);
        } catch (apiError) {
          console.error('Failed to clear cart in database:', apiError);
          // Continue with local storage even if API fails
        }
      }
      
      // Clear from localStorage
      clearCartFromStorage();
      
      // Return success
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: [],
    // Id of the customer's active backend cart (set by fetchCustomerCart)
    cartId: null,
    totals: calculateCartTotals([]),
    isOpen: false,
    loading: false,
    error: null,
  availablePromotions: [],
  promotionsLoaded: false,
    
    // Selection state for checkout
    selectedItems: [], // Array of itemKeys that are selected for checkout
    
    // Shipping information
    shippingAddress: null,
    billingAddress: null,
    
    // Checkout state
    checkoutStep: 0, // 0: Cart, 1: Shipping, 2: Payment, 3: Confirmation
    paymentMethod: null,
    
    // Wishlist
    wishlist: JSON.parse(localStorage.getItem('wishlist') || '[]'),
  },
  reducers: {
    // Cart item management (addToCart is now an async thunk)

    // Refresh per-SFT price / minimum slabs on a cart item from the latest product response
    updateCartItemProductInfo: (state, action) => {
      const { itemKey, pricePerSft, minOrderSlabs, lots } = action.payload;
      const item = state.items.find(i => i.itemKey === itemKey);
      if (!item) return;
      if (pricePerSft != null) item.pricePerSft = pricePerSft;
      if (minOrderSlabs != null) item.minOrderSlabs = minOrderSlabs;
      if (lots && lots.length > 0) item.sftLots = lots;
      state.totals = calculateCartTotals(state.items);
      saveCartToStorage(state.items);
    },
    
    // Selection management
    toggleItemSelection: (state, action) => {
      const itemKey = action.payload;
      const index = state.selectedItems.indexOf(itemKey);
      
      if (index > -1) {
        // Item is selected, remove it
        state.selectedItems.splice(index, 1);
      } else {
        // Item is not selected, add it
        state.selectedItems.push(itemKey);
      }
    },
    
    selectAllItems: (state) => {
      state.selectedItems = state.items.map(item => item.itemKey);
    },
    
    deselectAllItems: (state) => {
      state.selectedItems = [];
    },
    
    removeFromCart: (state, action) => {
      const itemKey = action.payload;
      state.items = state.items.filter(item => item.itemKey !== itemKey);
      
      // Remove from selected items if it was selected
      state.selectedItems = state.selectedItems.filter(key => key !== itemKey);
      
      // Recalculate totals
      state.totals = calculateCartTotals(state.items);
      
      // Save to localStorage
      saveCartToStorage(state.items);
    },
    
    loadAvailablePromotions: (state) => {
      state.availablePromotions = loadAvailablePromotionsFromStorage();
      state.promotionsLoaded = true;
    },
    
    // Load cart for specific user
    loadUserCart: (state, action) => {
      const userId = action.payload;
      const userCartItems = getCartFromStorage(userId);
      state.items = userCartItems;
      state.totals = calculateCartTotals(userCartItems);
    },
    
    // Cart UI management
    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },
    
    openCart: (state) => {
      state.isOpen = true;
    },
    
    closeCart: (state) => {
      state.isOpen = false;
    },
    
    // Checkout management
    setCheckoutStep: (state, action) => {
      state.checkoutStep = action.payload;
    },
    
    nextCheckoutStep: (state) => {
      if (state.checkoutStep < 3) {
        state.checkoutStep += 1;
      }
    },
    
    previousCheckoutStep: (state) => {
      if (state.checkoutStep > 0) {
        state.checkoutStep -= 1;
      }
    },
    
    setShippingAddress: (state, action) => {
      state.shippingAddress = action.payload;
    },
    
    setBillingAddress: (state, action) => {
      state.billingAddress = action.payload;
    },
    
    setPaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
    },
    
    // Wishlist management
    addToWishlist: (state, action) => {
      const product = action.payload;
      
      const existingIndex = state.wishlist.findIndex(item => item.id === product.id);
      
      if (existingIndex === -1) {
        const wishlistItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          discountedPrice: product.discountedPrice,
          image: getCartImage(product),
          category: product.category,
          addedAt: new Date().toISOString(),
        };
        
        state.wishlist.push(wishlistItem);
        localStorage.setItem('wishlist', JSON.stringify(state.wishlist));
      }
    },
    
    removeFromWishlist: (state, action) => {
      const productId = action.payload;
      state.wishlist = state.wishlist.filter(item => item.id !== productId);
      localStorage.setItem('wishlist', JSON.stringify(state.wishlist));
    },
    
    clearWishlist: (state) => {
      state.wishlist = [];
      localStorage.setItem('wishlist', JSON.stringify([]));
    },

    // Update lot count for a lot booking item in the cart (+/- buttons)
    updateLotBookingLotCount: (state, action) => {
      const { itemKey, uiLotCount } = action.payload;
      const idx = state.items.findIndex(item => item.itemKey === itemKey);
      if (idx < 0) return;
      const item = state.items[idx];
      if (!item.isLotBooking) return;

      const lots = item.lots || [];
      const availableLots = lots.filter(l => (parseInt(l.lotNos) || 0) > 0);
      const clamped = Math.max(1, Math.min(uiLotCount, availableLots.length || 1));
      const selectedLots = availableLots.slice(0, clamped);

      const lotSftOf = (lot) => {
        if (lot.lotSft) return parseFloat(lot.lotSft) || 0;
        return ((parseFloat(lot.lotLength) || 0) * (parseFloat(lot.lotWidth) || 0) * (parseInt(lot.lotNos) || 0)) / 144;
      };

      const backendQty = selectedLots.reduce((s, l) => s + (parseInt(l.lotNos) || 0), 0);
      const selectedSft = selectedLots.reduce((s, l) => s + lotSftOf(l), 0);
      const pricePerSft = item.originalPrice || 0;
      const totalLotPrice = selectedSft * pricePerSft;
      const advancePct = item.paymentSplit?.advancePercentage || 10;
      const advanceAmt = totalLotPrice * advancePct / 100;

      state.items[idx].uiLotCount = clamped;
      state.items[idx].quantity = backendQty;
      state.items[idx].lotSft = selectedSft;
      state.items[idx].totalLotPrice = totalLotPrice;
      state.items[idx].paymentSplit = {
        ...item.paymentSplit,
        advanceAmount: advanceAmt,
        remainingAmount: totalLotPrice - advanceAmt,
        totalAmount: totalLotPrice,
      };

      state.totals = calculateCartTotals(state.items);
      saveCartToStorage(state.items);
    },

    // Reset cart (e.g. on logout)
    resetCart: (state) => {
      state.items = [];
      state.cartId = null;
      state.selectedItems = [];
      state.totals = calculateCartTotals([]);
    },
    
    moveToCartFromWishlist: (state, action) => {
      const productId = action.payload;
      const wishlistItem = state.wishlist.find(item => item.id === productId);
      
      if (wishlistItem) {
        // Remove from wishlist
        state.wishlist = state.wishlist.filter(item => item.id !== productId);
        localStorage.setItem('wishlist', JSON.stringify(state.wishlist));
        
        // Add to cart (this will be handled by addToCart action)
        // The component should dispatch addToCart separately
      }
    },
    
    // Error handling
    setError: (state, action) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    // Apply coupon/discount
    applyCoupon: (state, action) => {
      const { code, discount, discountAmount: discountAmountValue, discountType, productIds = [], sellerId, promotionId, promotionTitle } = action.payload;
      const normalizedProductIds = productIds.map((pid) => String(pid));

      const baseTotals = calculateCartTotals(state.items);

      // Calculate base subtotal for applicable items (without GST)
      const applicableSubtotal = state.items.reduce((sum, item) => {
        if (
          normalizedProductIds.length > 0 &&
          !normalizedProductIds.includes(String(item.id ?? item._id))
        ) {
          return sum;
        }
        return sum + getCartItemAmount(item);
      }, 0);

      if (applicableSubtotal <= 0) {
        return;
      }

      let finalDiscountAmount = 0;
      if (discountType === 'percentage') {
        finalDiscountAmount = (applicableSubtotal * discount) / 100;
      } else if (discountType === 'amount') {
        finalDiscountAmount = Number(discountAmountValue) || 0;
      } else if (discountType === 'fixed') {
        finalDiscountAmount = discount;
      }

      // Don't allow discount to exceed subtotal
      finalDiscountAmount = Math.min(finalDiscountAmount, baseTotals.subtotal);

      const newSubtotal = baseTotals.subtotal - finalDiscountAmount;
      const newTax = newSubtotal * 0.18;
      const newShipping = baseTotals.shipping;
      const newTotal = newSubtotal + newTax + newShipping;

      state.totals = {
        subtotal: Math.round(newSubtotal * 100) / 100,
        tax: Math.round(newTax * 100) / 100,
        shipping: Math.round(newShipping * 100) / 100,
        total: Math.round(newTotal * 100) / 100,
        discount: Math.round(finalDiscountAmount * 100) / 100,
        couponCode: code,
        discountMeta: {
          discountType,
          value: discountType === 'amount' ? discountAmountValue : discount,
          discountAmount: discountType === 'amount' ? discountAmountValue : 0,
          productIds: normalizedProductIds,
          sellerId,
          promotionId,
          promotionTitle,
        },
      };
    },
    
    removeCoupon: (state) => {
      const baseTotals = calculateCartTotals(state.items);
      state.totals = { 
        ...baseTotals,
        discount: 0,
        couponCode: null,
        discountMeta: null
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        const { product, quantity = 1, specifications = {} } = action.payload;
        const productId = product?.id ?? product?._id;
        const uniqueProductKey = productId ?? product?.slug ?? product?.sku ?? product?.name ?? 'unknown_product';

        // Create unique item key. Lot booking items include lotId so the same product
        // added with two different lots creates two distinct cart entries.
        const itemKey = (product.isLotBooking && product.selectedLotId)
          ? `${uniqueProductKey}_lot_${product.selectedLotId}`
          : `${uniqueProductKey}_${JSON.stringify(specifications)}`;

        const existingItemIndex = state.items.findIndex(
          item => item.itemKey === itemKey
        );

        if (existingItemIndex >= 0) {
          // For advance booking or lot booking products, replace the existing item instead of adding quantity
          if (product.isAdvanceBooking || product.isLotBooking) {
            state.items[existingItemIndex].quantity = quantity;
            // Update other booking-specific properties
            state.items[existingItemIndex].originalPrice = product.originalPrice;
            state.items[existingItemIndex].price = product.price;
            state.items[existingItemIndex].isAdvanceBooking = product.isAdvanceBooking;
            state.items[existingItemIndex].isLotBooking = product.isLotBooking;
            state.items[existingItemIndex].advancePercentage = product.advancePercentage;
            state.items[existingItemIndex].paymentSplit = product.paymentSplit;
            state.items[existingItemIndex].lotRequestData = product.lotRequestData;
            state.items[existingItemIndex].lotId = product.selectedLotId;
            state.items[existingItemIndex].lotName = product.selectedLotName;
            state.items[existingItemIndex].lotSft = product.lotSft;
            state.items[existingItemIndex].totalLotPrice = product.totalLotPrice;
            state.items[existingItemIndex].lots = product.lots || state.items[existingItemIndex].lots || [];
            state.items[existingItemIndex].uiLotCount = product.uiLotCount || 1;
          } else {
            // Update quantity if item already exists (normal products)
            state.items[existingItemIndex].quantity += quantity;
            if (getCartImage(product)) state.items[existingItemIndex].image = getCartImage(product);
            if (product.pricePerSft != null) state.items[existingItemIndex].pricePerSft = product.pricePerSft;
            if (product.minOrderSlabs != null) state.items[existingItemIndex].minOrderSlabs = product.minOrderSlabs;
            const productLots = getProductLots(product);
            if (productLots.length > 0) state.items[existingItemIndex].sftLots = productLots;
          }
        } else {
          // Add new item to cart
          const newItem = {
            itemKey,
            id: productId,
            name: product.name,
            price: product.price,
            discountedPrice: product.discountedPrice,
            image: getCartImage(product),
            category: product.category,
            subCategory: product.subCategory,
            sellerId: product.sellerId,
            sellerName: product.sellerName,
            priceUnit: product.priceUnit,
            stockQuantity: product.stockQuantity,
            maximumOrderQuantity: product.maximumOrderQuantity,
            // Per-SFT price from the product response (item.price is the per-slab price)
            pricePerSft: product.pricePerSft,
            // Minimum slabs derived from the product's minimumOrderQuantity (SFT)
            minOrderSlabs: product.minOrderSlabs,
            // Product lots, used to compute the SFT covered by the chosen slabs
            sftLots: getProductLots(product),
            specifications,
            quantity,
            addedAt: new Date().toISOString(),
            // Add booking-specific properties if it's an advance booking or lot booking
            ...(product.isAdvanceBooking && {
              originalPrice: product.originalPrice,
              isAdvanceBooking: product.isAdvanceBooking,
              advancePercentage: product.advancePercentage
            }),
            ...(product.isLotBooking && {
              originalPrice: product.originalPrice,
              isLotBooking: product.isLotBooking,
              advancePercentage: product.advancePercentage,
              paymentSplit: product.paymentSplit,
              lotRequestData: product.lotRequestData,
              lotSft: product.lotSft,
              totalLotPrice: product.totalLotPrice,
              lotId: product.selectedLotId,
              lotName: product.selectedLotName,
              lots: product.lots || [],
              uiLotCount: product.uiLotCount || 1,
            })
          };
          
          state.items.push(newItem);
          
          // Auto-select the new item for checkout
          if (!state.selectedItems.includes(itemKey)) {
            state.selectedItems.push(itemKey);
          }
        }
        
        // Recalculate totals
        state.totals = calculateCartTotals(state.items);
        
        // Save to localStorage
        saveCartToStorage(state.items);
        
        state.error = null;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCartItemQuantity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItemQuantity.fulfilled, (state, action) => {
        state.loading = false;
        const { itemKey, quantity } = action.payload;
        
        const itemIndex = state.items.findIndex(item => item.itemKey === itemKey);
        
        if (itemIndex >= 0) {
          if (quantity <= 0) {
            // Remove item if quantity is 0 or negative
            state.items.splice(itemIndex, 1);
          } else {
            // Update quantity
            const item = state.items[itemIndex];
            
            // Check against stock and order limits
            const maxQuantity = Math.min(
              item.stockQuantity,
              item.maximumOrderQuantity || item.stockQuantity
            );
            
            state.items[itemIndex].quantity = Math.max(
              Math.min(quantity, maxQuantity),
              Math.min(item.minOrderSlabs || 1, maxQuantity)
            );
          }
          
          // Recalculate totals
          state.totals = calculateCartTotals(state.items);
          
          // Save to localStorage
          saveCartToStorage(state.items);
        }
        
        state.error = null;
      })
      .addCase(updateCartItemQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCustomerCart.fulfilled, (state, action) => {
        const serverCart = action.payload?.cart;
        state.cartId = serverCart?.id || null;
        const serverItems = serverCart?.items || [];

        // The backend cart is the source of truth for which products are in the cart and their
        // slab counts; local items keep their extra UI fields (lots, lot-booking details, …).
        const merged = [];
        serverItems.forEach((si) => {
          const isLot = !!(si.lotBooking ?? si.isLotBooking);
          const localMatches = state.items.filter(li =>
            String(li.id) === String(si.productId) && !!li.isLotBooking === isLot);
          if (localMatches.length === 0) {
            merged.push(cartItemFromServer(si));
          } else if (isLot) {
            // Backend keeps one entry per product; locally each booked lot is its own item
            merged.push(...localMatches);
          } else {
            const local = localMatches[0];
            merged.push({
              ...local,
              image: si.productImage || local.image,
              quantity: Number(si.quantity) || local.quantity,
              selectedSft: Number(si.selectedSft) > 0 ? Number(si.selectedSft) : local.selectedSft,
            });
          }
        });

        state.items = merged;
        const keys = new Set(merged.map(i => i.itemKey));
        state.selectedItems = state.selectedItems.filter(k => keys.has(k));
        state.totals = calculateCartTotals(state.items);
        saveCartToStorage(state.items);
      })
      .addCase(removeCartItem.fulfilled, (state, action) => {
        const itemKey = action.payload;
        state.items = state.items.filter(item => item.itemKey !== itemKey);
        state.selectedItems = state.selectedItems.filter(key => key !== itemKey);
        state.totals = calculateCartTotals(state.items);
        saveCartToStorage(state.items);
      })
      .addCase(clearCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.loading = false;
        state.items = [];
        state.selectedItems = [];
        state.totals = calculateCartTotals([]);
        clearCartFromStorage();
        state.error = null;
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Still clear local state even if API call fails
        state.items = [];
        state.selectedItems = [];
        state.totals = calculateCartTotals([]);
        clearCartFromStorage();
      })
      // Clear cart immediately when user logs out
      .addCase('auth/logoutUser/fulfilled', (state) => {
        state.items = [];
        state.cartId = null;
        state.selectedItems = [];
        state.totals = calculateCartTotals([]);
        clearCartFromStorage();
      });
  },
});

export const {
  updateCartItemProductInfo,
  removeFromCart,
  loadUserCart,
  resetCart,
  toggleCart,
  openCart,
  closeCart,
  setCheckoutStep,
  nextCheckoutStep,
  previousCheckoutStep,
  setShippingAddress,
  setBillingAddress,
  setPaymentMethod,
  applyCoupon,
  removeCoupon,
  addToWishlist,
  removeFromWishlist,
  moveToWishlist,
  moveFromWishlist,
  clearWishlist,
  // New selection actions
  toggleItemSelection,
  selectAllItems,
  deselectAllItems,
  loadAvailablePromotions,
  updateLotBookingLotCount,
} = cartSlice.actions;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotals = (state) => state.cart.totals;
export const selectCartItemCount = (state) =>
  state.cart.items.reduce((total, item) => total + item.quantity, 0);
export const selectIsCartOpen = (state) => state.cart.isOpen;
export const selectWishlist = (state) => state.cart.wishlist;
export const selectIsInWishlist = (productId) => (state) =>
  state.cart.wishlist.some(item => item.id === productId);
export const selectIsInCart = (productId) => (state) =>
  state.cart.items.some(item => item.id === productId);

// New selection selectors
export const selectSelectedItems = (state) => state.cart.selectedItems;
export const selectSelectedCartItems = (state) => 
  state.cart.items.filter(item => state.cart.selectedItems.includes(item.itemKey));
export const selectSelectedItemsCount = (state) => state.cart.selectedItems.length;
export const selectIsItemSelected = (itemKey) => (state) =>
  state.cart.selectedItems.includes(itemKey);
export const selectAllItemsSelected = (state) =>
  state.cart.items.length > 0 && state.cart.selectedItems.length === state.cart.items.length;

export default cartSlice.reducer;